import {
  buildComplexIngredientName,
  getIngredientAndSubRecipeDisplays,
  getIngredientDisplays,
  getSubrecipeWeightInGrams
} from "@/services/ExportLabel.service";
import IngredientDisplay from "../Ingredient/IngredientDisplay";
import { RecipeCompositionMeasurement } from "../Recipes/RecipeCompositionMeasurement";
import { AdditionalContent } from "./AdditionalContent";
import { ContainsLessThanPercentOfContent } from "./ContainsLessThanPercentOfContent";
import FreeTextPlaceholder from "./FreeTextPlaceholder";
import { HeaderLanguageTranslations } from "./HeaderLanguageTranslations";
import { LabelTypeMetadata } from "./LabelTypeMetadata";
import { NutritionCountry } from "./NutritionCountry";
import { NutritionLabel } from "./NutritionLabel";
import {CaffeinatedProductType} from "@/models/Cautionary/CaffeinatedProductType";
import {CautionaryFoodType} from "@/models/Cautionary/CautionaryFoodType";
import {DishRecipeCommon} from "@/models/DishRecipeCommon";
import {LabelTypes} from "@/models/NutritionLabels/LabelTypes";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";

export class ExportLabelState {
  allergens: { id: string; isChecked: boolean; primaryLanguageName: string; secondaryLanguageName: string; }[];
  dietary: number[];
  nutritionLabelType: LabelTypes;
  mayContain: AdditionalContent;
  cautionaryMessage: AdditionalContent;
  containsLessThanPercentOf: ContainsLessThanPercentOfContent;
  headerLabelTranslations: HeaderLanguageTranslations;
  labelPreview: string;
  fopLabelPreview: string;
  bopCautionaryPreview: string;
  fopCautionaryPreview: string;
  includeIngredients: boolean;
  renderExpandedSubrecipes: boolean;
  loading: boolean;
  disableDownload: boolean;
  freeTextPlaceholders: FreeTextPlaceholder[];
  isIE: boolean;
  fopNutritionLabelId: number;
  hasValidCautionary: boolean;
  labelTypes: {
    [LabelTypeMetadata.FrontOfPack]: NutritionalFrontOfPackRenderData,
    [LabelTypeMetadata.CautionaryFrontOfPack]: CautionaryFrontOfPackRenderData,
    [LabelTypeMetadata.CautionarySideOfPack]: CautionarySideOfPackRenderData;
  };
  nutritionCountry: NutritionCountry | null;
  nutritionLabel: NutritionLabel | null;
  primaryLanguage: LabelLanguage | null;
  ingredientAndSubRecipeDisplaysModel : {
    combinedSubRecipeList : IngredientDisplay[];
    expandedSubRecipeList : IngredientDisplay[];
  };
  savedDisplays: IngredientDisplay[];
  savedNutritionLabelId: number;
  savedSubRecipeDisplays: IngredientDisplay[];

  // Used to store value of placeholders of saved label
  textPlaceholdersOfSavedLabel: FreeTextPlaceholder[] | null;

  constructor() {
    this.allergens = [
      {
        id: "Celery",
        isChecked: false,
        primaryLanguageName: "Celery",
        secondaryLanguageName: "",
      },
      {
        id: "Lupin",
        isChecked: false,
        primaryLanguageName: "Lupin",
        secondaryLanguageName: "",
      },
      {
        id: "Sesame Seed",
        isChecked: false,
        primaryLanguageName: "Sesame Seed",
        secondaryLanguageName: "",
      },
      {
        id: "Wheat",
        isChecked: false,
        primaryLanguageName: "Wheat",
        secondaryLanguageName: "",
      },
      {
        id: "Crustaceans",
        isChecked: false,
        primaryLanguageName: "Crustaceans",
        secondaryLanguageName: "",
      },
      {
        id: "Milk",
        isChecked: false,
        primaryLanguageName: "Milk",
        secondaryLanguageName: "",
      },
      {
        id: "Shelfish",
        isChecked: false,
        primaryLanguageName: "Shelfish",
        secondaryLanguageName: "",
      },
      {
        id: "Egg",
        isChecked: false,
        primaryLanguageName: "Egg",
        secondaryLanguageName: "",
      },
      {
        id: "Molluscs",
        isChecked: false,
        primaryLanguageName: "Molluscs",
        secondaryLanguageName: "",
      },
      {
        id: "Soy",
        isChecked: false,
        primaryLanguageName: "Soy",
        secondaryLanguageName: "",
      },
      {
        id: "Fish",
        isChecked: false,
        primaryLanguageName: "Fish",
        secondaryLanguageName: "",
      },
      {
        id: "Mustard",
        isChecked: false,
        primaryLanguageName: "Mustard",
        secondaryLanguageName: "",
      },
      {
        id: "Sulphites",
        isChecked: false,
        primaryLanguageName: "Sulphites",
        secondaryLanguageName: "",
      },
      {
        id: "Gluten",
        isChecked: false,
        primaryLanguageName: "Gluten",
        secondaryLanguageName: "",
      },
      {
        id: "Peanut",
        isChecked: false,
        primaryLanguageName: "Peanut",
        secondaryLanguageName: "",
      },
      {
        id: "Tree Nuts",
        isChecked: false,
        primaryLanguageName: "Tree Nuts",
        secondaryLanguageName: "",
      },
    ];
    this.dietary = [];
    this.mayContain = {
      primaryLanguageAdditionalContent: "",
      secondaryLanguageAdditionalContent: "",
    };
    this.cautionaryMessage = {
      primaryLanguageAdditionalContent: "",
      secondaryLanguageAdditionalContent: "",
    };
    this.containsLessThanPercentOf = {
      primaryLanguageContainsLessThanText: "Contains less than",
      secondaryLanguageContainsLessThanText: "",

      primaryLanguagePercentOfText: "percent of",
      secondaryLanguagePercentOfText: "",

      thresholdPercent: 2.0
    };
    this.headerLabelTranslations = {
      ingredientsPrimaryLanguageTranslation: "Ingredients",
      ingredientsSecondaryLanguageTranslation: "",

      containsPrimaryLanguageTranslation: "Contains",
      containsSecondaryLanguageTranslation: "",

      mayContainPrimaryLanguageTranslation: "May contain",
      mayContainSecondaryLanguageTranslation: "",

      cautionaryMessagePrimaryLanguageTranslation: "Caution",
      cautionaryMessageSecondaryLanguageTranslation: "",
    };
    this.nutritionLabelType = null;
    this.labelPreview = "";
    this.fopLabelPreview = "";
    this.bopCautionaryPreview = "";
    this.fopCautionaryPreview = "";
    this.includeIngredients = true;
    this.renderExpandedSubrecipes = false;
    this.loading = false;
    this.disableDownload = true;
    this.freeTextPlaceholders = [];
    this.isIE = false;
    this.fopNutritionLabelId = 0;
    this.hasValidCautionary = false;
    this.labelTypes = {
      [LabelTypeMetadata.FrontOfPack]: {
        fopFormat: 1,
        pdsSize: 1,
        includeInDownload: true
      },
      [LabelTypeMetadata.CautionaryFrontOfPack]: {
        fopFormat: 1,
        pdsSize: 1,
        includeInDownload: true
      },
      [LabelTypeMetadata.CautionarySideOfPack]:{
        foodType: 0,
        caffeinatedType: 0,
        quantityInPackage: 0,
        labelWidth: 450
      }
    };
    this.nutritionCountry = null;
    this.nutritionLabel = null;
    this.primaryLanguage = LabelLanguage.English;
    this.ingredientAndSubRecipeDisplaysModel = {
      combinedSubRecipeList: [],
      expandedSubRecipeList: []
    };
    this.savedDisplays = [];
    this.savedNutritionLabelId = null;
    this.savedSubRecipeDisplays = [];

    this.textPlaceholdersOfSavedLabel = null;
  };

  /**
   * Gets a formatted list of ingredient displays
   * NOTE: This is for Recipes only
   */
  getIngredientDisplays(recipe: DishRecipeCommon): IngredientDisplay[] {
    // If we're in a dish recipe, ingredients will be undefined, so we use an empty array
    return getIngredientDisplays(recipe.ingredients || [], this.savedDisplays);
  }

  /**
   * Gets a formatted list of sub-recipe displays
   * These take the format of Subrecipe (ingredient1, ingredient2, ingredient3, ...)
   */
  getSubRecipeDisplays(recipe: DishRecipeCommon, subrecipeMeasurements: RecipeCompositionMeasurement[]): IngredientDisplay[] {
    // check if there is a recipe
    // if not, will return empty aRRAY
    const displays: IngredientDisplay[] = [];

    if (recipe) {
      // map the ingredients
      recipe.childRecipes.map((recipe) => {
        // See if you can find savedDisplay in savedDisplays for the recipe Id
        const savedDisplay = this.savedSubRecipeDisplays.find(
          (x) => x.recipeId == recipe.recipe.id
        );

        const subRecipeWeight = getSubrecipeWeightInGrams(recipe, subrecipeMeasurements);

        // If found, push that one
        if (savedDisplay) {
          displays.push({
            recipeId: savedDisplay.recipeId,
            displayTextInPrimaryLanguage: savedDisplay.displayTextInPrimaryLanguage,
            displayTextInSecondaryLanguage: savedDisplay.displayTextInSecondaryLanguage,
            displayText: savedDisplay.displayText,
            isAllergen: savedDisplay.isAllergen,
            allergenVisualState: savedDisplay.allergenVisualState,
            allergenFormattingStyle: savedDisplay.allergenFormattingStyle,
            allergenFormattingColor: savedDisplay.allergenFormattingColor,
            allergenFormattingHighlightColor: savedDisplay.allergenFormattingHighlightColor,
            allergenFormattingFontFamily: savedDisplay.allergenFormattingFontFamily,
            allergenFormattingFontSize: savedDisplay.allergenFormattingFontSize,
            isBranded: false,
            weightInGrams: subRecipeWeight,
          });
        }

        else {
          let displayTextInPrimaryLanguage = buildComplexIngredientName(recipe.recipe);

          displays.push({
            recipeId: recipe.recipe.id,
            displayTextInPrimaryLanguage: displayTextInPrimaryLanguage,
            displayTextInSecondaryLanguage: "",
            isAllergen: false,
            isBranded: false,
            weightInGrams: subRecipeWeight,
          });
        }
      });
    }

    return displays;
  }

  // Merge ingredientDisplay and subRecipeDisplay
  getIngredientAndSubRecipeDisplays(
    recipe: DishRecipeCommon,
    subrecipeMeasurements: RecipeCompositionMeasurement[],
    renderExpandedSubrecipes : boolean
  ): IngredientDisplay[] {
    return getIngredientAndSubRecipeDisplays(
      this.getIngredientDisplays(recipe),
      recipe,
      this.getSubRecipeDisplays(recipe, subrecipeMeasurements),
      subrecipeMeasurements,
      renderExpandedSubrecipes
    );
  }
}

export type NutritionalFrontOfPackRenderData = {
  fopFormat: number;
  pdsSize: number;
  includeInDownload: boolean;
};

export type CautionaryFrontOfPackRenderData = {
  fopFormat: number;
  pdsSize: number;
  includeInDownload: boolean;
};

export type CautionarySideOfPackRenderData = {
  foodType: CautionaryFoodType;
  caffeinatedType: CaffeinatedProductType;
  quantityInPackage: number;
  labelWidth: number;
};